import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { IntlProvider } from "react-intl";
import { RouterProvider } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ConfigContext, useConfigQuery } from "./config.tsx";
import {
  applyDocumentLocale,
  installPublishedLocaleData,
  loadMessages,
  loadRestMessages,
  markAllMessagesReady,
  type MessageBundle,
  type Messages,
} from "./i18n.ts";
import { router } from "./router.tsx";
import type { Typography } from "@paginae/blocks/typography";

/** Typing aids (ADR-0044), loaded just after startup: they are not needed to show the app. */
const TypingShortcuts = lazy(() => import("./components/TypingAids.tsx"));
function useTypingAids(settings: Typography | null | undefined) {
  useEffect(() => {
    let stop: (() => void) | null = null;
    let cancelled = false;
    void import("./components/TypingAids.tsx").then((m) => {
      if (!cancelled) stop = m.installTypingAids();
    });
    return () => {
      cancelled = true;
      stop?.();
    };
  }, []);
  useEffect(() => {
    void import("./editor/typing-core.ts").then((m) => m.setTypingSettings(settings));
  }, [settings]);
}

export function App() {
  const config = useConfigQuery();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<Messages | null>(null);
  /** The locale whose remaining messages are merged in (loaded after the first render). */
  const [restFor, setRestFor] = useState<string | null>(null);
  const locale = config.data?.locale;
  const bundles = config.data?.messages;
  // The published versions in use: a new one is loaded when they change, as on the next visit.
  const bundlesKey = JSON.stringify(bundles ?? []);
  const localeDataKey = JSON.stringify(config.data?.localeData ?? null);

  const dir = config.data?.locales.find((l) => l.tag === locale)?.dir ?? "ltr";
  // Typing aids wherever people type (ADR-0044), with their own settings.
  useTypingAids(config.data?.user?.typography);
  // The theme they chose (ADR-0087); following the device needs nothing loaded.
  const theme = config.data?.user?.theme ?? "system";
  const themed = useRef(false);
  useEffect(() => {
    if (theme === "system" && !themed.current) return;
    themed.current = true;
    void import("./theme-mode.ts").then((m) => m.applyTheme(theme));
  }, [theme]);

  useEffect(() => {
    if (locale) applyDocumentLocale(locale, dir);
  }, [locale, dir]);

  // Messages load only when the language changes, not on every refresh of the configuration
  // (signing in refreshes it): a reload would briefly drop the messages loaded after startup.
  useEffect(() => {
    if (!locale) return;
    let cancelled = false;
    const chain = JSON.parse(bundlesKey) as MessageBundle[];
    const localeData = JSON.parse(localeDataKey) as MessageBundle | null;
    void Promise.all([loadMessages(chain), installPublishedLocaleData(localeData)]).then(
      async ([core]) => {
        if (cancelled) return;
        setMessages(core);
        const rest = await loadRestMessages(chain);
        if (cancelled) return;
        setMessages({ ...rest, ...core });
        setRestFor(locale);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [locale, bundlesKey, localeDataKey]);

  // Screens loaded on demand open only once the merged catalogue has been rendered.
  useEffect(() => {
    if (restFor !== null) markAllMessagesReady();
  }, [restFor]);

  if (config.isError) {
    // Rendered before any catalogue is available, so this one string is not localised.
    return (
      <main id="main" className="centered">
        <p role="alert">Pāginæ could not reach its server. Please reload the page.</p>
      </main>
    );
  }
  if (!config.data || !messages || !locale) {
    return <div className="boot" aria-busy="true" />;
  }
  return (
    <ConfigContext value={config.data}>
      <IntlProvider locale={locale} messages={messages} defaultLocale="en">
        <Suspense fallback={null}>
          <TypingShortcuts />
        </Suspense>
        <RouterProvider router={router} context={{ queryClient }} />
      </IntlProvider>
    </ConfigContext>
  );
}
